import { addChainToUrl } from 'lib/utils/utils'
import { useCallback } from 'react'
import { NavigateOptions, Path, useLocation, useNavigate } from 'react-router-dom'

import { useConnection } from './useConnection'

export const useChainAwareNavigate = () => {
  const baseNavigate = useNavigate()
  const location = useLocation()
  const { chainId } = useConnection()

  const cleanChainFromUrl = (url: string): string => {
    if (!url || !url.includes('chain=')) return url

    return url
      .replace(/[?&]chain=[^&?#]*/g, '') // Remove chain params
      .replace(/[?&]{2,}/g, '&') // Fix multiple separators
      .replace(/^[?&]/, '?') // Ensure proper start
      .replace(/[?&]$/, '') // Remove trailing separators
  }

  const normalizeSearch = (search: string): string => {
    if (!search) return ''
    const cleaned = search.replace(/^\?+/, '').replace(/\?+/g, '?')
    return cleaned ? `?${cleaned}` : ''
  }

  const parseUrl = (url: string) => {
    try {
      const tempUrl = new URL(url, 'http://temp.com')
      return {
        pathname: tempUrl.pathname,
        search: tempUrl.search || undefined,
        hash: tempUrl.hash || undefined,
      }
    } catch {
      const hashIndex = url.indexOf('#')
      const hash = hashIndex !== -1 ? url.substring(hashIndex) : undefined
      const withoutHash = hashIndex !== -1 ? url.substring(0, hashIndex) : url

      const queryIndex = withoutHash.indexOf('?')
      const pathname = queryIndex !== -1 ? withoutHash.substring(0, queryIndex) : withoutHash
      const search = queryIndex !== -1 ? withoutHash.substring(queryIndex) : undefined

      return { pathname, search, hash }
    }
  }

  return useCallback(
    (to: string | Partial<Path> | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        return baseNavigate(to)
      }

      if (typeof to === 'string') {
        const cleanUrl = cleanChainFromUrl(to)
        const urlWithChain = addChainToUrl(cleanUrl, chainId)
        return baseNavigate(urlWithChain, options)
      }

      if (to.pathname) {
        const cleanPath = cleanChainFromUrl(to.pathname)

        const normalizedSearch = normalizeSearch(to.search || '')

        let fullUrl = cleanPath
        if (normalizedSearch) {
          fullUrl += normalizedSearch
        }
        if (to.hash) {
          fullUrl += to.hash.startsWith('#') ? to.hash : `#${to.hash}`
        }

        const urlWithChain = addChainToUrl(fullUrl, chainId)
        const parsed = parseUrl(urlWithChain)

        return baseNavigate(
          {
            pathname: parsed.pathname,
            search: parsed.search,
            hash: parsed.hash,
          },
          options
        )
      }

      if (to.search && !to.pathname) {
        const cleanPath = cleanChainFromUrl(location.pathname)

        const normalizedSearch = normalizeSearch(to.search)

        const urlWithChain = addChainToUrl(cleanPath + normalizedSearch, chainId)
        const parsed = parseUrl(urlWithChain)

        return baseNavigate(
          {
            pathname: parsed.pathname,
            search: parsed.search,
          },
          options
        )
      }

      return baseNavigate(to, options)
    },
    [baseNavigate, chainId, location.pathname]
  )
}
